import * as React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

export interface MaskedValueProps {
  /** The already-rendered empty-value markup (e.g. an em-dash) to wrap. */
  children: React.ReactNode;
  /**
   * True when this specific value was withheld by a permission gate rather
   * than genuinely absent. Decide this per row/value (e.g. from a response's
   * own withheld marker), never from a caller-level permission flag alone —
   * see `field-visibility`'s "Restricted values are masked, not removed from
   * the layout".
   */
  masked: boolean;
  /** Translated explanation shown on hover/focus, and to screen readers via Radix's aria-describedby. */
  explanation: string;
  /** Tooltip open delay in ms. Defaults to 300. */
  delayDuration?: number;
}

/**
 * Wraps an already-rendered empty-value cell in a hover/focus tooltip
 * explaining that the value was withheld by permission, instead of hiding or
 * altering the mask's appearance. A masked value SHALL look identical to a
 * genuinely absent one except for this explanation (field-visibility spec).
 *
 * No `apps/mockups` screen demonstrates this primitive: it renders identically
 * to the empty-value markup it wraps (an em-dash) except for the hover/focus
 * tooltip, so a static mockup screenshot would show nothing a plain em-dash
 * mockup doesn't already show — the tooltip interaction is exercised by this
 * component's own unit tests and by the catalog/merged-table/unit-detail e2e
 * coverage instead (ui-new-component-without-mockup).
 */
export function MaskedValue({
  children,
  masked,
  explanation,
  delayDuration = 300,
}: MaskedValueProps): React.ReactNode {
  if (!masked) return children;
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} data-testid="masked-value">
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent>{explanation}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Masks an already-rendered field value when its underlying data is `null`
 * AND the caller-supplied `withheld` marker is `true` — the single
 * field-visibility predicate shared by every masked cell/field renderer
 * (catalog columns, the merged offer table's columns, the unit detail
 * sidebar, and the generic offer field-column builder). Extracted here so a
 * future change to the predicate (e.g. handling `undefined` vs `null`, or
 * adding a withheld reason) reaches every surface identically instead of
 * risking one copy silently diverging (qual-dry-violation).
 *
 * `withheld` MUST be the marker for THIS value's own dimension — e.g.
 * `costsWithheld` for a cost field, `supplierWithheld` for a supplier field
 * — and never a caller-level permission flag or the other dimension's
 * marker; the two are resolved independently (field-visibility spec). A
 * genuinely-absent value on a fully-visible row renders unmasked.
 */
export function maskFieldValue(
  value: unknown,
  withheld: boolean,
  explanation: string,
  rendered: React.ReactNode,
): React.ReactNode {
  const masked = value == null && withheld;
  return (
    <MaskedValue masked={masked} explanation={explanation}>
      {rendered}
    </MaskedValue>
  );
}
